import type { Component, ReducedManifest } from '@wix/thunderbolt-becky-types'
import type { CompProps, IPropsStore } from '@wix/thunderbolt-symbols'
import { getDefaultCompId } from './hoverBoxUtils'

export const REPEATER_DELIMITER = '__'

export const isRepeatedComponent = (compId: string) => compId.includes(REPEATER_DELIMITER)

export const getDisplayedId = (originalId: string, itemId: string) => `${originalId}${REPEATER_DELIMITER}${itemId}`

export const getInflatedItemId = (itemId: string, outerItemId: string) =>
	outerItemId ? `${itemId}${REPEATER_DELIMITER}${outerItemId}` : itemId

export const getFullId = (id: string) => id.split(REPEATER_DELIMITER, 1)[0]

export const getTemplateItemId = (id: string) => id.split(REPEATER_DELIMITER, 2)[1]

export const getDisplayedItemId = (id: string) => {
	const [_compId, ...restItemIds] = id.split(REPEATER_DELIMITER)
	return restItemIds.join(REPEATER_DELIMITER)
}

export const getInnerMostItemId = (itemId: string) => itemId.split(REPEATER_DELIMITER, 1)[0]

export const getTemplateCompIdAndRepeaterScope = (id: string) => {
	const [templateCompId, ...itemIds] = id.split(REPEATER_DELIMITER)
	return { templateCompId, scope: itemIds }
}

export const getFullItemId = (id: string) => {
	const delimiterIndex = id.indexOf(REPEATER_DELIMITER)
	return delimiterIndex !== -1 ? id.slice(delimiterIndex + REPEATER_DELIMITER.length) : ''
}

export const getOuterItemId = (id: string) => {
	const [_compId, _innerItemId, ...outerItemIds] = id.split(REPEATER_DELIMITER)
	return outerItemIds.join(REPEATER_DELIMITER)
}

export const getItemOuterItemId = (id: string) => {
	const [_innerItemId, ...outerItemIds] = id.split(REPEATER_DELIMITER)
	return outerItemIds.join(REPEATER_DELIMITER)
}

export const isDisplayedOnly = (id: string) => getFullId(id) !== id

/**
 * The id to look a component up by in the RAW (original) structure.
 *
 * The raw structure holds only base components — repeater items and `:hover` variants are both
 * synthesized later, so a scoped id like `comp-x__item1` or `comp-x:hover` never matches it. Strip
 * both scopes to reach the base component the variant was derived from.
 *
 * `getFullId` alone is not enough: it splits on the repeater delimiter and leaves `:hover` in place,
 * so hover variants miss the lookup. Keep this the single definition both engines call, so the two
 * lookups cannot drift apart.
 */
export const getOriginalStructureCompId = (id: string) => (id ? getFullId(getDefaultCompId(id)) : id)

export const isRepeater = (compType: string) => {
	const tail = compType.split('.').pop()
	return tail === 'Repeater' || tail === 'DynamicRepeater'
}

export const isRepeaterComponent = (component: Pick<Component, 'componentType' | 'type'>): boolean =>
	component.componentType === 'Repeater' || component.type === 'RepeaterContainer'

/**
 * A builder component is a repeater only when its reduced manifest declares a `template` container.
 * Slot-only builder containers do not count.
 */
export const hasBuilderTemplateContainer = (reducedManifest: ReducedManifest | undefined): boolean =>
	!!reducedManifest &&
	Object.values(reducedManifest.elementData).some((elementData) =>
		Object.values(elementData.containersData ?? {}).some((container) => container.containerType === 'template')
	)

/**
 * Builder components are classified exclusively by their manifest; non-builder components are
 * classified by their raw structure type.
 */
export const isRepeaterComponentWithManifest = (
	component: Component,
	reducedManifest: ReducedManifest | undefined
): boolean =>
	component.builderType ? hasBuilderTemplateContainer(reducedManifest) : component.type === 'RepeaterContainer'

export const hasTemplateContainer = (compProps: CompProps) => {
	const templateContainers = (compProps && compProps.templateContainers) ?? {}
	return Object.keys(templateContainers).length > 0
}

export const isRepeatedComponentOfTemplate = (templateCompId: string) => {
	const templatePrefix = `${templateCompId}${REPEATER_DELIMITER}`
	return (compId: string) => compId.startsWith(templatePrefix)
}

export const getRepeatedCompSelector = (compId: string) =>
	// @ts-ignore
	globalThis.shouldUseClassSelectors ? `.${compId}` : `[id^="${compId}${REPEATER_DELIMITER}"]`

// 'itemInner__itemMiddle__itemOuter' => [ 'itemInner__itemMiddle__itemOuter', 'itemInner__itemMiddle', 'itemInner' ]
export const getRepeaterItemsAncestorChain = (itemId: string) => {
	const repeaterItemsAncestorChain: Array<string> = []
	itemId
		.split(REPEATER_DELIMITER)
		.forEach((item, index) =>
			index === 0
				? repeaterItemsAncestorChain.push(item)
				: repeaterItemsAncestorChain.push(getDisplayedId(repeaterItemsAncestorChain[index - 1], item))
		)
	return repeaterItemsAncestorChain.reverse()
}

export const getRepeaterItemsFromProps = (
	repeaterId: string,
	getProps: IPropsStore['get']
): Array<string> | undefined => {
	const items = getProps(repeaterId)?.items as Array<string> | undefined
	if (!isDisplayedOnly(repeaterId)) {
		return items
	}
	if (items) {
		const { scope } = getTemplateCompIdAndRepeaterScope(repeaterId)
		return items.map((itemId) => {
			return getInflatedItemId(itemId, scope.join(REPEATER_DELIMITER))
		})
	}
	const { templateCompId: repeaterTemplate, scope } = getTemplateCompIdAndRepeaterScope(repeaterId)
	scope.pop()
	const outerScope = scope.join(REPEATER_DELIMITER)
	return getRepeaterItemsFromProps(
		outerScope ? getDisplayedId(repeaterTemplate, outerScope) : repeaterTemplate,
		getProps
	)
}
